import { AsyncPipe, NgClass } from '@angular/common';
import { Component, Inject, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { TsSnackbarService } from '../../services/ts-snackbar.service';
import { ICellEditorParams } from 'ag-grid-enterprise';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { BehaviorSubject, debounceTime, noop, switchMap, tap } from 'rxjs';
import { TSIconModule } from '../../ts-icon/ts-icon.module';
import { LOOKUP_SERVICE_TOKEN } from '../../cross-cutting/util';
import { TsButtonModule } from '../../ts-button/ts-button.module';
import { TsPipesModule } from '../../cross-cutting/pipes/ts-pipes.module';
import { LookupContract, LookupResult } from '../../cross-cutting/models';

export type RecordCellEditorParams = ICellEditorParams & {
  multiSelect: boolean;
  recordType?: string;
  options?: LookupResult[];
  valueMode?: 'recordString' | 'idOnly';
  optionLabelKey?: 'label' | 'name';
};

@Component({
  standalone: true,
  imports: [
    AsyncPipe,
    MatProgressSpinnerModule,
    TsPipesModule,
    FormsModule,
    ReactiveFormsModule,
    TSIconModule,
    TsButtonModule,
    MatTooltipModule,
    NgClass,
  ],
  template: `
    <div class="container" [style]="{ width: width }">
      <input
        #searchInput
        placeholder="Search..."
        autocomplete="off"
        [formControl]="searchControl"
        (keydown)="onKeyDown($event)"
      />
      <ul>
        @if (lookupLoading | async) {
          <mat-progress-spinner class="spinner" diameter="24" mode="indeterminate"> </mat-progress-spinner>
        }
        @for (result of lookupResults | async; track result; let i = $index) {
          <li
            (click)="toggle(result)"
            [attr.data-index]="i"
            [ngClass]="{ selected: isSelected(result), active: i === activeIndex }"
          >
            @if (isSelected(result)) {
              <ts-icon class="mr-2" icon="ts_check"></ts-icon>
            }
            <span>{{ result.label }}{{ result.company ? ' | ' + result.company.name : '' }}</span>
          </li>
        }
        @if ((emptyResults | async) && canCreate) {
          <li class="p-2">
            <ts-button
              [loading]="addLoading | async"
              (buttonClick)="create()"
              [matTooltip]="'Add a record named &quot;' + searchControl.value + '&quot;'"
              matTooltipPosition="right"
              matTooltipShowDelay="500"
              >{{ (addLoading | async) ? 'Adding' : 'Add ' + params.colDef.headerName }}</ts-button
            >
          </li>
        }
      </ul>
    </div>
  `,
  styles: [
    `
      .container {
        background: white;
        padding: 12px 0px 0px 0px;
        height: 220px;

        input {
          padding: 4px 16px 8px 16px;
          border-bottom: 1px solid #e2e8f0;
          width: 100%;
        }

        ul {
          overflow: auto;
          height: 100%;
          background: white;

          li {
            display: flex;
            align-items: center;
            padding: 8px 16px;
            font-size: 10pt;

            &.selected {
              font-weight: 500;
            }

            &.active {
              background: #f5f5f5;
            }

            &:hover:not(.p-2) {
              background: #f5f5f5;
              cursor: pointer;
            }

            ts-button {
              button {
                width: 100%;
              }
            }
          }
        }
      }
    `,
  ],
})
export class TsAgMultiSelectEditor implements ICellEditorAngularComp, AfterViewInit {
  @ViewChild('searchInput') inputRef: ElementRef<HTMLInputElement>;
  searchControl = new FormControl();
  lookupLoading = new BehaviorSubject<boolean>(false);
  lookupResults = new BehaviorSubject<LookupResult[]>([]);
  emptyResults = new BehaviorSubject<boolean>(false);
  selectedLookupResults = new BehaviorSubject<LookupResult[]>([]);
  lastSearch = '';
  params: RecordCellEditorParams = null;
  width = 'auto';
  addLoading = new BehaviorSubject<boolean>(false);
  canCreate = false;
  private allOptions: LookupResult[] = [];
  activeIndex = -1;

  constructor(
    @Inject(LOOKUP_SERVICE_TOKEN) private lookupService: LookupContract,
    private snackBar: TsSnackbarService,
  ) {}

  ngAfterViewInit(): void {
    this.focusSearch();
  }

  agInit(params: RecordCellEditorParams): void {
    this.params = {
      valueMode: 'recordString',
      optionLabelKey: 'label',
      ...params,
    };
    this.width = params.column.getActualWidth() + 'px';

    if (params.options?.length) {
      const labelKey = this.params.optionLabelKey ?? 'label';
      this.allOptions =
        params.options.map((o: any) => ({
          id: o.id,
          label: o[labelKey] ?? o.name ?? o.label ?? String(o.id),
          company: (o as any).company,
        })) ?? [];
      this.lookupResults.next(this.allOptions);
    }
    this.resetActiveIndex();
    if (Array.isArray(params.value)) {
      if (typeof params.value[0] === 'string' && params.value[0]?.includes('|')) {
        this.selectedLookupResults.next(params.value.map((item) => this.recordStringToLink(item)));
      } else if (params.options?.length) {
        const selected = params.value
          .map((id) => this.allOptions.find((o) => String(o.id) === String(id)))
          .filter(Boolean) as LookupResult[];
        this.selectedLookupResults.next(selected);
      }
    } else if (params.value != null) {
      if (typeof params.value === 'string' && params.value.includes('|')) {
        this.selectedLookupResults.next([this.recordStringToLink(params.value)]);
      } else if (params.options?.length) {
        const found = this.allOptions.find((o) => String(o.id) === String(params.value));
        if (found) this.selectedLookupResults.next([found]);
      }
    }

    if (!params.options?.length) {
      this.searchControl.valueChanges
        .pipe(
          tap(() => this.emptyResults.next(false)),
          debounceTime(300),
          tap(() => this.lookupLoading.next(true)),
          switchMap((value: string) => {
            this.lastSearch = value;
            return this.lookupService.lookFor({ query: value, relatedTo: params.recordType });
          }),
          tap((result: LookupResult[]) => {
            this.emptyResults.next(result.length === 0);
            const merged = [...this.selectedLookupResults.value, ...result];
            const unique = merged.filter((item, index, arr) => arr.findIndex((i) => i.id === item.id) === index);
            this.lookupResults.next(unique);
            this.lookupLoading.next(false);
          }),
        )
        .subscribe(noop);
      this.searchControl.setValue('');
      this.focusSearch();
    } else {
      this.searchControl.valueChanges
        .pipe(
          debounceTime(150),
          tap((q: string) => {
            const query = (q ?? '').toLowerCase();
            if (!query) {
              this.lookupResults.next(this.allOptions);
              this.resetActiveIndex();
              return;
            }
            const filtered = this.allOptions.filter((o) => (o.label ?? '').toLowerCase().includes(query));
            this.lookupResults.next(filtered);
            this.resetActiveIndex();
          }),
        )
        .subscribe(noop);
    }

    this.canCreate =
      !!this.params.recordType &&
      this.lookupService.doesSupportCreate(
        this.params.colDef.context?.related_to_type,
        this.params.colDef.context?.meta?.record_type,
      );
  }

  isPopup?(): boolean {
    return true;
  }

  getPopupPosition?(): 'over' | 'under' | undefined {
    return 'under';
  }

  remove(result: LookupResult): void {
    this.selectedLookupResults.next(this.selectedLookupResults.value.filter(({ id }) => id !== result.id));
    if (!this.params.options?.length) {
      const firstIndex = this.lookupResults.value.findIndex(({ id }) => id === result.id);
      this.lookupResults.next(this.lookupResults.value.filter((_, index) => index !== firstIndex));
    }
  }

  isSelected(result: LookupResult): boolean {
    return this.selectedLookupResults.value.some(({ id }) => id === result.id);
  }

  toggle(result: LookupResult): void {
    const selected = this.isSelected(result);
    if (this.params.multiSelect) {
      if (selected) {
        this.selectedLookupResults.next(this.selectedLookupResults.value.filter(({ id }) => id !== result.id));
      } else {
        this.selectedLookupResults.next([...this.selectedLookupResults.value, result]);
      }
      return;
    }
    if (selected) {
      this.selectedLookupResults.next([]);
      return;
    } else {
      this.selectedLookupResults.next([result]);
      this.params.stopEditing();
      return;
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    const list = this.lookupResults.value || [];
    if (!list.length) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeIndex = (this.activeIndex + 1) % list.length;
      this.scrollActiveIntoView();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex = (this.activeIndex - 1 + list.length) % list.length;
      this.scrollActiveIntoView();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (this.activeIndex >= 0 && this.activeIndex < list.length) {
        this.toggle(list[this.activeIndex]);
      }
    }
  }

  private resetActiveIndex(): void {
    const list = this.lookupResults.value || [];
    this.activeIndex = list.length ? 0 : -1;
    this.scrollActiveIntoView();
  }

  private scrollActiveIntoView(): void {
    if (this.activeIndex < 0) return;
    const el = document.querySelector(`li[data-index="${this.activeIndex}"]`);
    (el as HTMLElement)?.scrollIntoView({ block: 'nearest' });
  }

  private focusSearch(): void {
    setTimeout(() => this.inputRef?.nativeElement?.focus(), 0);
  }

  getValue(): any {
    const results = this.selectedLookupResults.value.map(({ id, label }) => ({ id, label }));
    const idOnly = this.params.valueMode === 'idOnly';

    if (!this.params.multiSelect) {
      const first = results[0];
      if (!first) return idOnly ? null : '';
      return idOnly ? first.id : `${first.id}|${first.label}`;
    }

    return idOnly ? results.map((r) => r.id) : results.map((r) => `${r.id}|${r.label}`);
  }

  hideSelected(value: LookupResult[], selectedLookupResults: LookupResult[]): LookupResult[] {
    return value.filter(({ id }) => !selectedLookupResults.map((result) => result.id).includes(id));
  }

  create(): void {
    const multiSelect = this.params.colDef.context.meta.multi_select;
    const recordType = this.params.colDef.context.meta.record_type;
    const fieldName = this.params.colDef.context.name;
    const relatedTo = this.params.colDef.context.related_to_type;

    if (multiSelect) {
      this.params.node.data[fieldName] = this.selectedLookupResults.value.map((x) => `${x.id}|${x.label}`);
    }

    this.addLoading.next(true);

    this.lookupService
      .createAndLink({
        name: this.searchControl.value,
        fieldName,
        recordType,
        relatedTo,
        linkedTo: this.params.node.data,
        multiSelect,
      })
      .pipe(tap(() => this.addLoading.next(false)))
      .subscribe(() => {
        this.searchControl.setValue('');
        this.snackBar.open('Record created successfully', 'success');
      });
  }

  private recordStringToLink(recordString: string): LookupResult {
    return {
      id: Number(recordString.substring(0, recordString.indexOf('|'))),
      label: recordString.substring(recordString.indexOf('|') + 1),
    };
  }
}
